import { Camera, Check } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { useT } from '../lib/i18n'
import { readPreference, setPreference } from '../lib/theme'
import { Bar } from '../ui/Bar'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Chip } from '../ui/Chip'
import { EmptyState } from '../ui/EmptyState'
import { Input } from '../ui/Input'
import { Mascot, type MascotState } from '../ui/Mascot'
import { NumberInput } from '../ui/NumberInput'
import { Ring } from '../ui/Ring'
import { Segmented } from '../ui/Segmented'
import { Sheet } from '../ui/Sheet'
import { Spinner } from '../ui/Spinner'
import { Stepper } from '../ui/Stepper'
import { ToastProvider, useToast } from '../ui/Toast'

/**
 * `/ui-kit` — every primitive, every state, both themes.
 *
 * This route is the review surface for the design system: a human opens it,
 * flips the theme, and looks. `npm run shots` captures it in both themes, and
 * `npm run ui:measure` sweeps it at every phone width alongside the product
 * routes — which is why it is in `scripts/routes.mjs` rather than being a
 * developer-only page nobody measures.
 *
 * It is deliberately not behind a flag. A kit that only exists in development
 * is a kit whose dark mode nobody has seen on a device.
 *
 * The strings here are sample data written for developers, and each one says so
 * at the point of use — see `sampleText` below. This file carries **no file-level
 * exemption from `check:i18n`**: it is scanned like any other, because the route
 * ships and a whole-file exemption also covers the copy nobody has written yet
 * (SUAP-0112). The phrase the guard's `EXEMPTION_MARKER` looks for is deliberately
 * not written anywhere in this file — a sentence saying the file is *not* exempt
 * would otherwise satisfy the "say why, in the file" requirement if someone ever
 * re-added it to `EXEMPT`. The locale
 * switch exists so the primitives can be reviewed in both languages, which is
 * the part that ships.
 *
 * SUAP-0103.
 */
/**
 * Specimen text: an identity function at runtime, and a marker to `check:i18n`.
 *
 * i18n-sample. This route renders every primitive against sample data — figures,
 * type specimens, section headings, four buttons that say what a button says.
 * None of that is product copy and none of it belongs in the dictionary; a
 * dictionary carrying `Sambal terasi, 14px, teks pendukung` is a dictionary
 * nobody trusts.
 *
 * It is marked one string at a time rather than by exempting the file, because
 * this route ships (`src/App.tsx`) and a file-level exemption also covers the
 * copy nobody has written yet. It had already swallowed a paragraph about what
 * the mascot may say about a user's body — a CLAUDE.md non-negotiable, rendered
 * on screen, invisible to the guard. That paragraph is now `t('mascot.policy')`,
 * and anything else real added here fails until someone decides which it is.
 *
 * **A primitive's label is never specimen data**, even here: `check:i18n`
 * reports those whether or not they are marked, which is why every `label=` on
 * this page reads `t(...)`. SUAP-0112.
 */
function sampleText(text: string) {
  return text
}

export function UiKit() {
  return (
    <ToastProvider>
      <UiKitBody />
    </ToastProvider>
  )
}

function UiKitBody() {
  const toast = useToast()
  const { t, locale, setLocale } = useT()
  const [theme, setTheme] = useState(readPreference())
  const [chip, setChip] = useState(true)
  const [segment, setSegment] = useState<'turun' | 'jaga' | 'naik'>('turun')
  const [portion, setPortion] = useState(150)
  const [weight, setWeight] = useState<number | null>(72.4)
  const [sheetOpen, setSheetOpen] = useState(false)

  function flipTheme() {
    const next = theme === 'dark' ? 'light' : 'dark'
    setPreference(next)
    setTheme(next)
  }

  return (
    <div className="flex flex-col gap-8 pb-8">
      <div className="flex items-center justify-between gap-3">
        {/* The figures on this page are sample data. A review pointed out that a
            2000 kcal ring and "Protein 82 / 120 g" look exactly like a real plan
            to anyone who reaches this route, so the page says so once, at the
            top, rather than leaving it to be inferred. */}
        <p className="text-sm text-ink-soft">
          {sampleText(
            'Setiap komponen, setiap keadaan — angka di halaman ini contoh, bukan datamu. Ganti tema untuk memeriksa keduanya.',
          )}
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" onClick={flipTheme}>
            {sampleText(theme === 'dark' ? 'Terang' : 'Gelap')}
          </Button>
          <Button variant="ghost" onClick={() => setLocale(locale === 'id' ? 'en' : 'id')}>
            {t('locale.switch')}
          </Button>
        </div>
      </div>

      <Section title={sampleText('Mascot — placeholder art, five states')}>
        <div className="flex flex-wrap items-end gap-4">
          {(['idle', 'happy', 'thinking', 'resting', 'curious'] as MascotState[]).map((state) => (
            <div key={state} className="flex flex-col items-center gap-1">
              <Mascot state={state} size={72} />
              <span className="text-xs text-ink-soft">{state}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-ink-soft">
          {sampleText('Garis putus-putus menandai gambar sementara — bukan karya final.')}{' '}
          {/* Not specimen data: this is the product's stated position on what the
              mascot may say, and CLAUDE.md non-negotiable 6 is the reason it says
              it. It rendered in Bahasa on the English UI until SUAP-0112. */}
          {t('mascot.policy')}
        </p>
      </Section>

      <Section title={sampleText('Type — Plus Jakarta Sans')}>
        <div className="flex flex-col gap-2">
          <p className="text-display-lg tabular-nums text-ink">1.847</p>
          <p className="text-display tabular-nums text-ink">1.847</p>
          <p className="text-display-sm tabular-nums text-ink">1.847</p>
        </div>
        <div className="flex flex-col gap-1">
          {/* text-xl exists in the scale and /ui-kit rendered none of it, so the
              line-height sweep could not see the one step that was left on
              Tailwind's 1.4. SUAP-0105 re-review. */}
          <p className="text-xl text-ink">{sampleText('Ringkasan hari ini — 20px, judul bagian')}</p>
          <p className="text-lg text-ink">{sampleText('Nasi goreng spesial — 18px, ukuran judul layar')}</p>
          <p className="text-base text-ink">{sampleText('Ayam bakar taliwang, 16px, ukuran teks utama')}</p>
          <p className="text-sm text-ink-soft">{sampleText('Sambal terasi, 14px, teks pendukung')}</p>
          <p className="text-xs text-ink-soft">{sampleText('12px, keterangan kecil')}</p>
        </div>
        {/* Bahasa borrows these from Dutch, Sanskrit and Arabic. The eye is the
            weaker half of this check: `npm run check:fonts` compares each
            codepoint's advance against a monospace and a serif fallback, which
            is what actually proves the glyph came from the webfont.

            ʿ U+02BF (‘ain) and ʼ U+02BC used to be on this line, under a
            comment claiming a box would mean the subset had not loaded. Neither
            is in Google's shipped latin-ext subset, despite both sitting inside
            the declared unicode-range — and macOS draws U+02BF from a system
            font, so there was never a box to see. The claim was unfalsifiable
            here and false on a device without that system font. SUAP-0105
            review. */}
        <p className="text-base text-ink">
          {sampleText('Diakritik: é è ê ë · à á â · ï î · ô ö · ü û · ç · ñ · ā ī ū · ĕ ŭ')}
        </p>
        <p className="text-sm text-ink-soft">
          {sampleText('Angka tabular tidak menggeser lebar: 1.111 · 1.888 · 0.000 · 9.999')}
        </p>
      </Section>

      <Section title={sampleText('Button')}>
        <div className="flex flex-wrap gap-2">
          <Button variant="ink">{sampleText('Simpan')}</Button>
          <Button variant="accent">{sampleText('Catat makan')}</Button>
          <Button variant="ghost">{sampleText('Batal')}</Button>
          <Button variant="danger">{sampleText('Hapus')}</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ink" loading>
            {sampleText('Menyimpan')}
          </Button>
          <Button variant="accent" disabled>
            {sampleText('Tidak aktif')}
          </Button>
          <Button variant="ink" icon={<Camera aria-hidden size={18} />}>
            {sampleText('Dengan ikon')}
          </Button>
        </div>
        <Button variant="ink" size="lg" block>
          {sampleText('Ukuran besar, selebar kolom')}
        </Button>
      </Section>

      <Section title={sampleText('Chip')}>
        <div className="flex flex-wrap gap-2">
          <Chip onSelect={() => setChip(!chip)} selected={chip} icon={chip ? <Check aria-hidden size={16} /> : undefined}>
            {sampleText('Sarapan')}
          </Chip>
          <Chip onSelect={() => undefined}>{sampleText('Makan siang')}</Chip>
          <Chip>{sampleText('Statis')}</Chip>
        </div>
      </Section>

      {/* The primitives' labels come from the dictionary, not from literals here.
          `sampleText()` marks this file's sample prose and section headings; it
          deliberately does not reach a string handed to a shipped component as a
          label or accessible-name prop, because switching the kit to English used
          to leave every one of those in Bahasa — an `aria-label` reading
          "Karbohidrat" beside an `aria-valuetext` reading "210 g of 210 target".
          SUAP-0110 third review, narrowed to per-string marking in SUAP-0112. */}
      <Section title={sampleText('Input dan angka')}>
        <Input label={t('field.email')} placeholder={t('field.emailExample')} type="email" />
        <Input label={t('field.name')} hint={t('field.nameHint')} defaultValue="Rizka" />
        <Input
          label={t('field.password')}
          type="password"
          error={t('field.passwordTooShort')}
          defaultValue="123"
        />
        <NumberInput
          label={t('field.weight')}
          value={weight}
          onChange={setWeight}
          unit="kg"
          hint={t('field.decimalHint')}
        />
      </Section>

      <Section title={sampleText('Stepper')}>
        <Stepper label={t('field.portion')} value={portion} onChange={setPortion} step={5} min={0} max={2000} unit="g" />
        <p className="text-xs text-ink-soft">
          {sampleText('Tahan tombol untuk mengulang: 400 ms jeda, lalu 8/detik, lalu 20/detik setelah dua detik.')}
        </p>
      </Section>

      <Section title={sampleText('Segmented')}>
        <Segmented
          label={t('goal.label')}
          value={segment}
          onChange={setSegment}
          options={[
            { value: 'turun', label: t('goal.down') },
            { value: 'jaga', label: t('goal.hold') },
            { value: 'naik', label: t('goal.up') },
          ]}
        />
      </Section>

      <Section title={sampleText('Card')}>
        <Card>
          <p className="text-sm text-ink">{sampleText('Kartu datar. Kedalaman dari tangga terang, bukan bayangan.')}</p>
        </Card>
        <Card onPress={() => toast.show(sampleText('Kartu ditekan'))}>
          <p className="text-sm text-ink">{sampleText('Kartu yang bisa ditekan — elemennya tombol, bukan div.')}</p>
        </Card>
      </Section>

      <Section title={sampleText('Ring — termasuk kelebihan target')}>
        <div className="flex flex-wrap items-end gap-4">
          {[
            { value: 0, caption: '0%' },
            { value: 900, caption: '45%' },
            { value: 1980, caption: '99%' },
            { value: 2000, caption: '100%' },
            { value: 2020, caption: '101%' },
            { value: 2800, caption: '140%' },
            { value: 6400, caption: '320%' },
          ].map((row) => (
            <Ring key={row.caption} value={row.value} target={2000} size={104} caption={row.caption} />
          ))}
        </div>
        <p className="text-xs text-ink-soft">
          {sampleText('Di atas target menggambar ke luar, bukan memutus cincin. Busur luar berhenti di satu putaran penuh.')}
        </p>
      </Section>

      <Section title={sampleText('Bar — garis target selalu terlihat')}>
        <Bar label={t('macro.protein')} value={82} target={120} unit="g" tone="protein" />
        <Bar label={t('macro.carbs')} value={210} target={210} unit="g" tone="carbs" />
        <Bar label={t('macro.fat')} value={78} target={60} unit="g" tone="fat" />
        <Bar label={t('macro.fibre')} value={12} target={30} unit="g" tone="fiber" />
      </Section>

      <Section title={sampleText('Sheet')}>
        <Button variant="ink" onClick={() => setSheetOpen(true)}>
          {sampleText('Buka sheet')}
        </Button>
        <Sheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t('sheet.editPortion')}
          footer={
            <Button variant="accent" block onClick={() => setSheetOpen(false)}>
              {sampleText('Simpan')}
            </Button>
          }
        >
          <div className="flex flex-col gap-4">
            <Stepper label={t('field.portion')} value={portion} onChange={setPortion} step={5} min={0} max={2000} unit="g" />
            <Input label={t('field.note')} placeholder={t('field.optional')} />
          </div>
        </Sheet>
      </Section>

      <Section title={sampleText('Toast')}>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => toast.show(sampleText('Tersimpan'))}>
            {sampleText('Netral')}
          </Button>
          <Button variant="ghost" onClick={() => toast.show(sampleText('Catatan hari ini lengkap'), 'good')}>
            {sampleText('Baik')}
          </Button>
          <Button variant="ghost" onClick={() => toast.show(sampleText('120 kkal di atas target'), 'over')}>
            {sampleText('Di atas target')}
          </Button>
        </div>
      </Section>

      <Section title={sampleText('EmptyState dan Spinner')}>
        <EmptyState
          emoji="🍚"
          title={t('diary.emptyTitle')}
          body={t('diary.emptyBody')}
          action={<Button variant="accent">{sampleText('Catat makan')}</Button>}
        />
        <div className="flex items-center gap-4">
          <Spinner size={16} />
          <Spinner size={24} />
          <Spinner size={36} />
        </div>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-soft">{title}</h2>
      {children}
    </section>
  )
}
